import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Ticket, ArrowRight, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { convStore } from '../lib/compareStore';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api`;

// Button 2 of the save email lands here. The link alone reveals nothing: the
// buyer's ticket number is required every time, on any device.
export default function TicketReturn() {
    const { linkToken } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const open = async () => {
        const given = code.trim();
        if (!given) return;
        setBusy(true); setError('');
        try {
            const { data } = await axios.post(`${API}/vehicle-ticket/${linkToken}/open`, { ticket_code: given });
            // Re-point this device at the buyer's existing thread so the live
            // chat and its 5s polling continue exactly as before.
            if (data.card_id && data.request_id) convStore.set(data.card_id, data.request_id);
            if (data.session_id) {
                try { sessionStorage.setItem(`yot_fi_session_${data.card_id}`, data.session_id); } catch (_e) { /* private mode */ }
            }
            const qs = new URLSearchParams();
            if (data.session_id) qs.set('s', data.session_id);
            qs.set('chat', '1');
            navigate(`/vehicle/${data.card_id}?${qs.toString()}`);
        } catch (e) {
            setError(e?.response?.status === 429 ? t('ticketReturn.locked') : t('ticketReturn.invalid'));
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6"
            data-testid="ticket-return-page" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-full max-w-sm space-y-5 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-emerald-400/30 bg-emerald-500/10">
                    <Ticket className="h-7 w-7 text-emerald-400" />
                </div>
                <h1 className="text-2xl font-extrabold leading-snug" data-testid="ticket-return-title">
                    {t('ticketReturn.title')}
                </h1>
                <p className="text-sm text-neutral-400">{t('ticketReturn.prompt')}</p>
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && open()}
                    placeholder={t('ticketReturn.placeholder')} data-testid="ticket-return-input"
                    className="w-full rounded-xl border border-neutral-700 bg-neutral-900 px-4 py-4 text-center text-lg font-semibold tracking-[0.2em] text-white placeholder:tracking-normal placeholder:text-neutral-600 focus:border-emerald-400/60 focus:outline-none" />
                {error && <p className="text-sm text-rose-400" data-testid="ticket-return-error">{error}</p>}
                <button type="button" onClick={open} disabled={busy || !code.trim()} data-testid="ticket-return-submit"
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-4 text-base font-bold text-neutral-950 transition-transform active:scale-[0.98] disabled:opacity-40 motion-reduce:active:scale-100">
                    {busy ? t('ticketReturn.opening') : t('ticketReturn.open')}
                    {!busy && <ArrowRight className="h-5 w-5" />}
                </button>
                <p className="flex items-start gap-2 text-left text-[11px] leading-relaxed text-neutral-500">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />{t('ticketReturn.privacy')}
                </p>
            </div>
        </div>
    );
}
